/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, Button, Center, Container, Flex, Stack, Text } from '@chakra-ui/core';
import { CheckIcon } from '@chakra-ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { AppointmentStatus } from '../../firebase/firestore/documents/appointmentStatus';
import { AppointmentType } from '../../firebase/firestore/documents/appointmentAvaiability';
import useSingleCandidate from '../../app/hooks/useSingleCandidate';
import { MeetTypeEnum, GuestMeetParams } from '../../routes/constants';
import { MeetGuestVerification } from '../guestMeet/MeetGuestVerification';
import { GreenRoomTestOutputDevice } from './GreenRoomTestOutputDevice';
import { LocalVideoPreview } from './LocalVideoPreview';
import { useMeetContext } from './MeetContext';
import { MeetParticipantVerification } from './MeetParticipantVerification';
import { ParticipantData, ValidMeetTokenResponse } from './MeetTokenResponse';
import {
  greenRoomPage,
  greenRoomRightPanel,
  greenRoomVideoBlock,
  greenRoomVideoInner,
  greenRoomVideoControls,
} from './MeetVide.styles';
import { AudioToggleButtonGreenRoom } from './twilio/AudioToggleButtonGreenRoom';
import { VideoToggleButton } from './twilio/VideoToggleButton';
import VideoEffects from './videoEffects/VideoEffects';
import { useAuthenticationStatus } from '../../app/hooks/useAuthenticationStatus';
import { AuthenticationStatus } from '../../app/models/appModel';
import { useIsMobile } from '../../app/hooks/useIsMobile';

type GreenRoomProps = {
  isLoading: boolean;
  joinGroupEventRoom: (participantInfo: ParticipantData) => void;
  groupEventInfo: ValidMeetTokenResponse | null;
  joinRoom: () => void;
  meetType: MeetTypeEnum;
  isCollaboratorFeatureEnabled: boolean;
  selectedAppointmentType: AppointmentType | null;
  setSelectedAppointmentType: (appointmentType: AppointmentType) => void;
};

export const GreenRoom = ({
  isLoading,
  joinGroupEventRoom,
  groupEventInfo,
  joinRoom,
  meetType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isCollaboratorFeatureEnabled,
  selectedAppointmentType,
  setSelectedAppointmentType,
}: GreenRoomProps): JSX.Element => {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { t } = useTranslation('meet');
  const { getAudioAndVideoTracks } = useMeetContext();
  const [isActive, setIsActive] = useState<boolean>(false);
  const authenticationStatus = useAuthenticationStatus();
  const { candidateId } = useParams<GuestMeetParams>();
  const singleCandidateData = useSingleCandidate(candidateId);
  const isCancelled = singleCandidateData?.appointment?.status === AppointmentStatus.cancelled;

  useEffect(() => {
    getAudioAndVideoTracks();
  }, [getAudioAndVideoTracks]);

  const showVideoPreview = useMemo(() => {
    return selectedAppointmentType === AppointmentType.video && authenticationStatus === AuthenticationStatus.AUTHENTICATED;
  }, [selectedAppointmentType, authenticationStatus]);

  return (
    <Box className="green-room-page" ref={mobilePanelRef}>
      <Container maxW="container.xl">
        <Flex css={greenRoomPage} justify="center" align="center" p={6}>
          {
            // istanbul ignore next
            showVideoPreview && (
              <Box css={greenRoomVideoBlock} className="green-room-video-block" ref={videoContainerRef}>
                <Box css={greenRoomVideoInner} className="green-room-video-inner">
                  <LocalVideoPreview />
                </Box>
                <Box css={greenRoomVideoControls} className="green-room-video-controls">
                  <AudioToggleButtonGreenRoom isLoading={isLoading} />
                  <VideoToggleButton isLoading={isLoading} />
                  <VideoEffects
                    isLoading={isLoading}
                    videoContainerRef={videoContainerRef}
                    rightPanelRef={isMobile ? mobilePanelRef : rightPanelRef}
                    marginLeft={0}
                  />
                </Box>
              </Box>
            )
          }

          <Box css={greenRoomRightPanel} className="green-room-right-panel" ref={rightPanelRef}>
            {isActive ? (
              // istanbul ignore next
              <GreenRoomTestOutputDevice onClose={/* istanbul ignore next */ () => setIsActive(!isActive)} />
            ) : (
              <Stack spacing={4}>
                {MeetTypeEnum.EVENT === meetType && (
                  <MeetParticipantVerification
                    groupEventInfo={groupEventInfo}
                    joinGroupEventRoom={joinGroupEventRoom}
                    isLoading={isLoading}
                  />
                )}
                {MeetTypeEnum.INTERVIEW === meetType &&
                  (isCollaboratorFeatureEnabled ? (
                    <MeetGuestVerification
                      joinRoom={joinRoom}
                      isLoading={isLoading}
                      setSelectedAppointmentType={setSelectedAppointmentType}
                    />
                  ) : (
                    <Text fontSize="lg">{t('collaboratorFeatureNotAvailable')}</Text>
                  ))}
                <Center>
                  <Button
                    size="sm"
                    leftIcon={<CheckIcon />}
                    colorScheme="blue"
                    variant="solid"
                    data-testid="activeBtn"
                    isDisabled={isCancelled}
                    onClick={/* istanbul ignore next */ () => setIsActive(!isActive)}
                  >
                    {t('checkYourDevice')}
                  </Button>
                </Center>
              </Stack>
            )}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};
